import React from "react";
import { motion } from "framer-motion";
import {
  Target, Radio, MousePointerClick, Webhook, Building2, Wallet, Globe, BarChart3,
  ArrowRight, Check, Send, Mail, Instagram, MessageCircle, Sparkles,
} from "lucide-react";
import { assetUrl, brandTextColor } from "@/lib/branding";

const FEATURE_ICONS = {
  offers: Target, streams: Radio, tracking: MousePointerClick, postbacks: Webhook,
  multitenant: Building2, payments: Wallet, domains: Globe, analytics: BarChart3,
};
const PERIOD = { monthly: "/mo", quarterly: "/qtr", semi_annual: "/6mo", yearly: "/yr", lifetime: " once" };

const up = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Shared landing renderer — used by the public landing page AND the admin live preview.
export function LandingView({ branding, plans = [], onLogin, onJoin, preview = false }) {
  const b = branding || {};
  const brand = b.brand || {};
  const land = b.landing || {};
  const accent = brand.accent_color || "#6366F1";
  const onAccentText = brandTextColor(accent);
  const logo = assetUrl(brand.logo_id);
  const hero = assetUrl(land.hero_image_id);
  const name = brand.name || "Affilix";
  const features = land.features || [];
  const advantages = land.advantages || [];
  const showPricing = land.show_pricing !== false && plans.length > 0;
  const M = preview ? "div" : motion.div;
  const anim = preview ? {} : { variants: up, initial: "hidden", whileInView: "show", viewport: { once: true, margin: "-60px" } };

  const contact = b.contact || {};
  const contactRows = [
    contact.telegram && { icon: Send, label: contact.telegram },
    contact.email && { icon: Mail, label: contact.email },
    contact.instagram && { icon: Instagram, label: contact.instagram },
    contact.discord && { icon: MessageCircle, label: contact.discord },
  ].filter(Boolean);

  return (
    <div className="min-h-full bg-[#050505] text-white" style={{ ["--brand"]: accent }}>
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-[rgba(5,5,5,0.7)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-2.5" data-testid="landing-brand">
            {logo ? <img src={logo} alt={name} className="h-7 w-auto object-contain" />
              : <span className="flex h-8 w-8 items-center justify-center rounded-lg font-display text-sm font-black" style={{ background: accent, color: onAccentText }}>{name.charAt(0)}</span>}
            <span className="font-display text-lg font-bold tracking-tight">{name}</span>
          </div>
          <div className="flex items-center gap-2.5">
            <button onClick={onLogin} data-testid="landing-login-btn" className="rounded-lg border border-white/10 px-4 py-2 text-sm text-white/90 transition-colors hover:bg-white/5">{land.cta_primary_label || "Login"}</button>
            <button onClick={onJoin} data-testid="landing-join-btn" className="rounded-lg px-4 py-2 text-sm font-medium transition-transform hover:scale-[0.98]" style={{ background: accent, color: onAccentText }}>{land.cta_secondary_label || "Get access"}</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute -top-40 left-1/2 h-[400px] w-[700px] -translate-x-1/2 rounded-full opacity-25 blur-[120px]" style={{ background: accent }} />
        <div className="relative mx-auto max-w-5xl px-6 pb-24 pt-20 text-center">
          <M {...anim}>
            {land.eyebrow && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs text-white/70">
                <Sparkles className="h-3 w-3" style={{ color: accent }} /> {land.eyebrow}
              </span>
            )}
            <h1 data-testid="landing-headline" className="mx-auto mt-6 max-w-3xl font-display text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">{land.headline || "The affiliate platform built for scale."}</h1>
            <p data-testid="landing-subheadline" className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/60">{land.subheadline || ""}</p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <button onClick={onJoin} className="group flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition-transform hover:scale-[0.98]" style={{ background: accent, color: onAccentText }}>
                {land.cta_secondary_label || "Get access"} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <button onClick={onLogin} className="rounded-xl border border-white/10 px-6 py-3 text-sm font-medium text-white/90 transition-colors hover:bg-white/5">{land.cta_primary_label || "Login"}</button>
            </div>
          </M>
          {hero && (
            <M {...anim} className="relative mt-14">
              <div className="pointer-events-none absolute inset-x-10 -top-6 bottom-0 rounded-3xl opacity-20 blur-3xl" style={{ background: accent }} />
              <img src={hero} alt="" className="relative mx-auto max-h-[420px] w-full rounded-2xl border border-white/[0.08] object-cover" />
            </M>
          )}
        </div>
      </section>

      {/* Advantages */}
      {advantages.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-16">
          <div className="grid gap-px overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.06] sm:grid-cols-3">
            {advantages.map((a, i) => (
              <M key={i} {...anim} custom={i} className="bg-[#0A0A0A] p-8">
                <h3 className="font-display text-lg font-semibold">{a.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/55">{a.text}</p>
              </M>
            ))}
          </div>
        </section>
      )}

      {/* Features bento */}
      {features.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-16">
          <div className="mb-10 max-w-xl">
            <h2 className="font-display text-3xl font-bold tracking-tight">Everything a network needs</h2>
            <p className="mt-2 text-sm text-white/55">One control room for the full affiliate lifecycle.</p>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {features.map((f, i) => {
              const Icon = FEATURE_ICONS[f.key] || Target;
              return (
                <M key={f.key || i} {...anim} custom={i}
                  className="group rounded-2xl border border-white/[0.08] bg-[#0A0A0A] p-6 transition-colors hover:border-white/20">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: `${accent}1a`, color: accent }}><Icon className="h-5 w-5" /></span>
                  <h3 className="mt-4 font-display text-base font-semibold">{f.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-white/50">{f.text}</p>
                </M>
              );
            })}
          </div>
        </section>
      )}

      {/* Pricing */}
      {showPricing && (
        <section className="mx-auto max-w-6xl px-6 py-16" data-testid="landing-pricing">
          <div className="mb-10 text-center">
            <h2 className="font-display text-3xl font-bold tracking-tight">Simple, scalable pricing</h2>
            <p className="mt-2 text-sm text-white/55">Pick a plan that grows with your network.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {plans.map((p, i) => (
              <M key={p.id || i} {...anim} custom={i} className="flex flex-col rounded-2xl border border-white/[0.08] bg-[#0A0A0A] p-6">
                <div className="text-sm font-medium text-white/70">{p.name}</div>
                <div className="mt-3 flex items-end gap-1">
                  <span className="font-display text-3xl font-bold">{p.price}</span>
                  <span className="pb-1 text-xs text-white/40">{p.currency}{PERIOD[p.period] || ""}</span>
                </div>
                {p.description && <p className="mt-2 text-xs text-white/45">{p.description}</p>}
                <ul className="mt-4 space-y-2">
                  {(p.features || []).map((ft, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs text-white/60"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: accent }} /> {ft}</li>
                  ))}
                </ul>
                <button onClick={onJoin} className="mt-6 rounded-lg border border-white/10 py-2 text-xs font-medium text-white/90 transition-colors hover:bg-white/5">Get started</button>
              </M>
            ))}
          </div>
        </section>
      )}

      {/* Contact CTA */}
      {(contact.support_name || contactRows.length > 0) && (
        <section className="mx-auto max-w-6xl px-6 py-16" data-testid="landing-contact">
          <div className="overflow-hidden rounded-3xl border border-white/[0.08] bg-[#0A0A0A] p-10 text-center" style={{ boxShadow: `inset 0 0 120px -60px ${accent}` }}>
            {assetUrl(contact.support_photo_id) && <img src={assetUrl(contact.support_photo_id)} alt="" className="mx-auto h-16 w-16 rounded-full object-cover ring-2 ring-white/10" />}
            <h2 className="mt-4 font-display text-2xl font-bold">Ready to get started?</h2>
            {contact.support_name && <p className="mt-1 text-sm text-white/55">Talk to {contact.support_name}{contact.support_title ? `, ${contact.support_title}` : ""}</p>}
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
              {contactRows.map((r, i) => (
                <span key={i} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-1.5 text-xs text-white/70"><r.icon className="h-3.5 w-3.5" style={{ color: accent }} /> {r.label}</span>
              ))}
            </div>
            <button onClick={onJoin} className="mt-6 rounded-xl px-6 py-3 text-sm font-semibold transition-transform hover:scale-[0.98]" style={{ background: accent, color: onAccentText }}>{land.cta_secondary_label || "Get access"}</button>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-white/[0.06]">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-3 px-6 py-8 text-xs text-white/40 sm:flex-row">
          <div className="flex items-center gap-2">
            {logo ? <img src={logo} alt={name} className="h-5 w-auto object-contain opacity-70" /> : <span className="font-display font-bold text-white/70">{name}</span>}
          </div>
          <span data-testid="landing-footer">{b.footer?.text || `© ${new Date().getFullYear()} ${name}. All rights reserved.`}</span>
        </div>
      </footer>
    </div>
  );
}
